import {
  category,
  createBankStatement,
  createBusiness,
  createEvent,
  createLogExpense,
  createNewInvoice,
  createRoles,
  getBankStatementsList,
  getDocumentsList,
  downloadDocuments,
  getExpenseList,
  getMileageList,
  getModules,
  getPermissions,
  getSalesList,
  inviteAccept,
  inviteDecline,
  inviteSend,
  login,
  recordMileage,
  signup,
  takeImageUploadExpenseUrl,
  getAnalyticsYears,
  getAnalyticsSummary,
  getRevenueBreakdown,
  getExpenseBreakdown,
  getCashPosition,
  getVatDetails,
  getKeyMetrics,
} from "./api";
import apiClient from "./apiClient";

export const loginApi = async (credentials) => {
  return await apiClient.post(login, credentials);
};

export const signupApi = async (credentials) => {
  return await apiClient.post(signup, credentials);
};

export const createBusinessApi = async (payload) => {
  return await apiClient.post(createBusiness, payload);
};

export const updateBusinessApi = async (id, payload) => {
  return await apiClient.put(`/accounter/business/update/${id}`, payload);
};

export const updateProfileApi = async (id, payload) => {
  return await apiClient.put(`/users/update/${id}`, payload);
};

export const createNewInvoiceApi = async (payload) => {
  return await apiClient.post(createNewInvoice, payload);
};

export const categoryApi = async (payload) => {
  return await apiClient.get(category);
};

export const saveExpenseApi = async (payload) => {
  return await apiClient.post(createLogExpense, payload);
};

export const takeImageUploadExpenseApi = async (payload) => {
  return await apiClient.post(takeImageUploadExpenseUrl, payload, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const takeImageUploadSalesApi = async (payload) => {
  return await apiClient.post(takeImageUploadExpenseUrl, payload, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const recordMileageApi = async (payload) => {
  return await apiClient.post(recordMileage, payload);
};

export const createBankStatementApi = async (payload) => {
  return await apiClient.post(createBankStatement, payload);
};

export const getExpenseListApi = async (payload) => {
  const { businessId, ...filters } = payload;
  return await apiClient.get(`${getExpenseList}/${businessId}`, { params: filters });
};

export const getSalesListApi = async (payload) => {
  const { businessId, ...filters } = payload;
  return await apiClient.get(`${getSalesList}/${businessId}`, { params: filters });
};

export const getMileageListApi = async (payload) => {
  const { businessId, ...filters } = payload;
  return await apiClient.get(`${getMileageList}/${businessId}`, { params: filters });
};

export const getBankStatementsListApi = async (payload) => {
  const { businessId, ...filters } = payload;
  return await apiClient.get(`${getBankStatementsList}/${businessId}`, { params: filters });
};

export const createEventApi = async (payload) => {
  return await apiClient.post(createEvent, payload);
};

export const listEventsApi = async (businessId, filters = {}) => {
  const queryParams = new URLSearchParams();

  if (filters.status) queryParams.append("status", filters.status);
  if (filters.priority) queryParams.append("priority", filters.priority);
  if (filters.eventType) queryParams.append("eventType", filters.eventType);
  if (filters.category) queryParams.append("category", filters.category);
  if (filters.startDate) queryParams.append("startDate", filters.startDate);
  if (filters.endDate) queryParams.append("endDate", filters.endDate);

  const queryString = queryParams.toString()
    ? `?${queryParams.toString()}`
    : "";

  try {
    const response = await apiClient.get(
      `/events/list/${businessId}${queryString}`,
    );
    return response.data;
  } catch (error) {
    console.error("Failed to fetch events:", error);
    throw new Error(error.response?.data?.message || "Failed to fetch events");
  }
};

export const updateEventApi = async (eventId, payload) => {
  try {
    const response = await apiClient.put(`/events/update/${eventId}`, payload);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to update event");
  }
};

export const deleteEventApi = async (eventId) => {
  try {
    const response = await apiClient.delete(`/events/delete/${eventId}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to delete event");
  }
};

// CRUD users api
export const inviteSendApi = async (payload) => {
  return await apiClient.post(inviteSend, payload);
};

export const updateInviteApi = async (id, payload) => {
  return await apiClient.put(`${inviteSend}/update/${id}`, payload);
};

export const inviteAcceptApi = async (payload) => {
  return await apiClient.post(inviteAccept, payload);
};

export const inviteDeclineApi = async (payload) => {
  return await apiClient.post(inviteDecline, payload);
};

export const inviteListApi = async (payload) => {
  return await apiClient.get(inviteSend);
};

// CRUD roles api
export const createRolesApi = async (payload) => {
  return await apiClient.post(createRoles, payload);
};

export const getRolesApi = async (payload) => {
  return await apiClient.get(createRoles);
};

export const getModulesApi = async (payload) => {
  return await apiClient.get(getModules);
};

export const getRolegetPermissionsApisApi = async (payload) => {
  return await apiClient.get(getPermissions);
};

export const getDocumentsListApi = async (payload) => {
  return await apiClient.get(getDocumentsList, { params: payload });
};

// Analytics API Calls
export const getAnalyticsYearsApi = async (businessId) => {
  return await apiClient.get(`${getAnalyticsYears}/${businessId}/years`);
};

export const getAnalyticsSummaryApi = async (businessId, params = {}) => {
  // params can include businessYearId, startDate, endDate
  return await apiClient.get(`${getAnalyticsSummary}/${businessId}/summary`, {
    params,
  });
};

export const getRevenueBreakdownApi = async (businessId, params = {}) => {
  return await apiClient.get(
    `${getRevenueBreakdown}/${businessId}/revenue-breakdown`,
    { params },
  );
};

export const getExpenseBreakdownApi = async (businessId, params = {}) => {
  return await apiClient.get(
    `${getExpenseBreakdown}/${businessId}/expense-breakdown`,
    { params },
  );
};

export const getCashPositionApi = async (businessId, params = {}) => {
  return await apiClient.get(
    `${getCashPosition}/${businessId}/cash-position`,
    { params },
  );
};

export const getVatDetailsApi = async (businessId, params = {}) => {
  return await apiClient.get(`${getVatDetails}/${businessId}/vat`, { params });
};

export const getKeyMetricsApi = async (businessId, params = {}) => {
  return await apiClient.get(`${getKeyMetrics}/${businessId}/key-metrics`, {
    params,
  });
};

export const getMediaSignedUrlApi = async (filename) => {
  return await apiClient.get(downloadDocuments, { params: { filename } });
};
